import type { ReactNode } from 'react';
import { useIsDesktop } from '../../hooks/useIsDesktop.js';
import { Underline, Sparkle, SparkleOutline, Waveform } from '../../components/doodles/Doodles.js';

/**
 * Marco responsive de las pantallas de auth.
 *
 * - Móvil/tablet: el form (`.auth-mobile-body`) llena la columna capada.
 * - Escritorio: split de dos columnas — panel hero editorial a la izquierda
 *   + EXACTAMENTE el mismo `.auth-mobile-body` a la derecha (cero duplicación
 *   de campos/lógica; el form se reusa tal cual entre ambos breakpoints).
 */
interface AuthLayoutProps {
  children: ReactNode;
}

const HERO_FEATURES = [
  'Coach de voz que te corrige en tiempo real',
  'Errores de gramática y pronunciación, explicados',
  'Sesiones cortas · a tu nivel · sin libreto',
] as const;

function AuthHero() {
  return (
    <div className="auth-hero-inner">
      <div className="auth-brand">
        <img className="auth-brand-lockup" src="/brand/orborix-speak-logo.svg" alt="orborix speak" />
      </div>

      <div className="auth-hero-copy">
        <h2 className="auth-hero-title">
          Hablá inglés{' '}
          <span className="auth-hero-spark">
            <span className="script">de verdad</span>
            <Underline color="var(--lime)" width={220} style={{ marginTop: 4 }} />
            <SparkleOutline
              color="rgba(167,139,250,0.5)"
              size={64}
              style={{ position: 'absolute', top: -28, right: -36 }}
            />
          </span>
        </h2>
        <p className="auth-hero-sub">
          Conversaciones reales con un coach que te escucha, te corrige y te
          arma el vocabulario nuevo a medida que hablás.
        </p>

        <ul className="auth-hero-features">
          {HERO_FEATURES.map((f, i) => (
            <li key={i}>
              <span className="auth-hero-bullet">
                <Sparkle color="var(--lime)" size={12} />
              </span>
              {f}
            </li>
          ))}
        </ul>
      </div>

      <div className="auth-hero-foot">
        <Waveform color="var(--lime)" size={20} />
        <span>speak.orborix.com</span>
      </div>
    </div>
  );
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const isDesktop = useIsDesktop();

  if (!isDesktop) {
    return (
      <div className="app auth-shell">
        <div className="auth-mobile-body">{children}</div>
      </div>
    );
  }

  return (
    <div className="app auth-shell auth-desktop">
      <aside className="auth-hero">
        <AuthHero />
      </aside>
      <div className="auth-desktop-form">
        <div className="auth-mobile-body">{children}</div>
      </div>
    </div>
  );
}
